/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { FF_MOCK_AUTH } from './feature-flag';

/**
 * Returns the list of required environment variable definitions.
 *
 * @returns {Array<{ name: string; description?: string }>} Variable definitions.
 */
function getRequiredVarDefinitions(): Array<{ name: string; description?: string }> {
  const useMockAuth = FF_MOCK_AUTH;

  const baseVars: Array<{ name: string; description?: string }> = [
    { name: 'REACT_APP_API_URL', description: 'API base URL' },
  ];

  if (useMockAuth) {
    return baseVars;
  }

  return [
    ...baseVars,
    { name: 'REACT_APP_AUTHORITY_URL', description: 'OIDC authority URL' },
    { name: 'REACT_APP_CLIENT_ID', description: 'OIDC client ID' },
  ];
}

/**
 * Checks whether an environment variable value is blank or missing.
 *
 * @param {string | undefined} value - The environment variable value.
 * @returns {boolean} True when the value is missing or empty.
 */
function isEnvVarMissing(value: string | undefined): boolean {
  return !value || value.trim() === '';
}

/**
 * Resolves an environment variable from runtime config (window.__ENV__) first,
 * falling back to build-time import.meta.env for dev mode.
 */
function resolveEnvVar(name: string): string | undefined {
  const windowEnv = typeof window !== 'undefined' && (window as any).__ENV__;
  return (windowEnv && windowEnv[name]) || import.meta.env[name] || undefined;
}

/**
 * Validates that all required environment variables are set.
 * Throws errors if any required variables are missing.
 */
export function validateRequiredEnvVars(): void {
  const requiredVars = getRequiredVarDefinitions();

  const errors = requiredVars
    .filter(({ name }) => isEnvVarMissing(resolveEnvVar(name)))
    .map(({ name, description }) =>
      `Missing required environment variable: ${name}${description ? ` (${description})` : ''}`
    );

  if (errors.length === 0) {
    return;
  }

  const missingNames = requiredVars
    .filter(({ name }) => isEnvVarMissing(resolveEnvVar(name)))
    .map(({ name }) => name);

  const errorMessage = [
    'Missing required environment variables:',
    ...errors,
    '',
    'Please ensure all required environment variables are set before running the application.',
    `Missing variables: ${missingNames.join(', ')}`,
  ].join('\n');

  console.error(errorMessage);
  throw new Error(errorMessage);
}

/**
 * Gets an environment variable value, throwing an error if it's not set.
 * Reads from window.__ENV__ (runtime config.js) first, falling back to
 * import.meta.env for local development.
 */
export function getRequiredEnvVar(name: string): string {
  const value = resolveEnvVar(name);
  if (isEnvVarMissing(value)) {
    throw new Error(
      `Required environment variable ${name} is not set. Please configure it before running the application.`
    );
  }
  return typeof value === 'string' ? value : String(value!);
}

/**
 * Gets an optional environment variable value, returning undefined if not set.
 * Reads from window.__ENV__ (runtime config.js) first, falling back to
 * import.meta.env for local development.
 */
export function getOptionalEnvVar(name: string): string | undefined {
  const value = resolveEnvVar(name);
  return isEnvVarMissing(value) ? undefined : String(value!);
}
